import axios from 'axios';
import { toast } from 'sonner';
import { getDesktopToken, setDesktopToken, clearDesktopToken } from './desktopToken';
import { getDeviceHeaders } from './desktopDevice';

// Resolve the backend base URL.
// In the cloud/web deployment, REACT_APP_BACKEND_URL is set at build time
// (e.g. https://yourdomain.com). In the offline desktop build, it's left empty
// so requests go to the same origin as the page (http://127.0.0.1:8001).
// We must explicitly fall back to '' instead of letting JS interpolate
// `undefined` into the URL — that breaks every API call silently.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// Add auth token to requests.
// The browser web app authenticates via the httpOnly `session_token` cookie
// the backend sets on login (withCredentials above attaches it automatically —
// never readable by JS, closing the localStorage-XSS token-theft gap).
// The DutiLyne Desktop Terminal (Electron, cross-origin to the cloud API)
// uses a Bearer token instead, since it also needs it to stay signed in
// through real connectivity drops. That token is held in memory and persisted
// by the Electron main process behind the OS keychain — see lib/desktopToken.js.
const isDesktop = () => typeof window !== 'undefined' && !!window.pharmxai;
export { isDesktop };

// Each portal (workspace, platform console, staff phone app) keeps its own session.
export const currentPortal = () => {
  const p = typeof window !== 'undefined' ? window.location.pathname : '/';
  if (p === '/platform' || p.startsWith('/platform/')) return 'platform';
  if (p === '/staff-app' || p.startsWith('/staff-app/')) return 'staff_app';
  return 'workspace';
};
export const userCacheKey = () => ({ platform: 'pharmxai_user_platform', staff_app: 'pharmxai_user_staffapp' }[currentPortal()] || 'pharmxai_user');
export const portalSignInPath = () => ({ platform: '/platform', staff_app: '/staff-app/login' }[currentPortal()] || '/login');

api.interceptors.request.use((config) => {
  config.headers['X-DutiLyne-Portal'] = currentPortal();
  if (isDesktop()) {
    const token = getDesktopToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Desktop identifies itself so the backend grants the longer
    // offline-safe sliding session window (see routes/auth.py::login).
    config.headers['X-Client-Type'] = 'desktop';
    // ...and identifies the physical till for the Terminal Register.
    Object.assign(config.headers, getDeviceHeaders());
  }
  return config;
});

// Raw fetch (streaming chat, PDF blobs) with the same auth as the axios client.
export const authFetch = async (url, init = {}) => {
  const headers = { 'X-DutiLyne-Portal': currentPortal(), ...(init.headers || {}) };
  if (isDesktop()) {
    const token = getDesktopToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    headers['X-Client-Type'] = 'desktop';
    Object.assign(headers, getDeviceHeaders());
  }
  const res = await fetch(url, { credentials: 'include', ...init, headers });
  const renewed = res.headers.get('x-renewed-token');
  if (isDesktop() && getDesktopToken() && renewed && renewed !== getDesktopToken()) {
    setDesktopToken(renewed);
  }
  return res;
};

// Handle 401 responses + harvest the X-Renewed-Token header for HIPAA
// sliding sessions. Backend issues a fresh JWT on every authenticated
// response (15-min idle timeout) — for the browser this also arrives as a
// renewed `session_token` cookie, so only the desktop client (which can't
// rely on cookies cross-origin) needs to swap its stored token manually.
api.interceptors.response.use(
  (response) => {
    const renewed = response?.headers?.['x-renewed-token'];
    if (isDesktop() && getDesktopToken() && renewed && renewed !== getDesktopToken()) {
      setDesktopToken(renewed);
    }
    const url = response?.config?.url || '';
    const billableAiCall = (
      url.includes('/ai/')
      && !url.includes('/ai/quota')
      && !url.includes('/ai/usage')
      && !url.includes('/ai/budget')
      && !url.includes('/ai/health')
    ) || url.includes('/pricing/ai-analysis');
    if (billableAiCall) {
      window.dispatchEvent(new Event('pharmxai-ai-usage-changed'));
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // A wrong shift-lock PIN is a 401 on a session that is still perfectly
      // valid — the lock screen handles it. Only a server-forced sign-out
      // (after too many tries) should tear the session down here.
      const isPinAttempt = (error.config?.url || '').includes('/auth/shift-unlock')
        && !error.response?.data?.force_logout;
      if (isPinAttempt) return Promise.reject(error);
      clearDesktopToken();
      localStorage.removeItem(userCacheKey());
      const path = window.location.pathname;
      // '/' is the public marketing home and '/platform' hosts DutiLyne's own
      // staff sign-in — neither may be bounced to the pharmacy login screen.
      const PUBLIC_PATHS = ['/login', '/plans', '/welcome', '/signup/pharmacy', '/partners', '/platform', '/platform-invite', '/staff-app/login', '/try-demo'];
      const isPublic = path === '/' || PUBLIC_PATHS.some(p => path === p || path.startsWith(p + '/'));
      if (!isPublic) {
        window.location.href = portalSignInPath();
      }
    }
    // 402 Payment Required → tier-limit gate hit. Convert the raw error
    // into a clean upgrade nudge with a one-click deep-link.
    if (error.response?.status === 402) {
      const detail = error.response?.data?.detail || 'Plan limit reached';
      // Suppress duplicate toasts for the same message
      if (window.__pxai_402_lastDetail !== detail) {
        window.__pxai_402_lastDetail = detail;
        setTimeout(() => { window.__pxai_402_lastDetail = null; }, 4000);
        if (!window.location.pathname.startsWith('/subscription')) {
          // Close any open Radix Dialog overlays so the toast action button
          // is actually clickable (overlays sit at z-50 with pointer-events: auto)
          try { document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true })); } catch (err) { console.warn('Failed to dismiss overlay:', err); }
          toast(detail, {
            description: 'Upgrade your plan to keep growing.',
            duration: 8000,
            action: {
              label: 'Upgrade now',
              onClick: () => {
                // Defer to next tick so sonner's own click handlers can finish
                // before we navigate — prevents the toast-close race that
                // cancels synchronous redirects.
                setTimeout(() => { window.location.assign('/subscription'); }, 50);
              },
            },
          });
        }
      }
    }
    return Promise.reject(error);
  }
);

// Offline queue
const SYNC_QUEUE_KEY = 'pharmxai_sync_queue';

export const addToSyncQueue = (operation) => {
  const queue = JSON.parse(localStorage.getItem(SYNC_QUEUE_KEY) || '[]');
  queue.push({ ...operation, timestamp: new Date().toISOString() });
  localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
};

export const getSyncQueue = () => {
  return JSON.parse(localStorage.getItem(SYNC_QUEUE_KEY) || '[]');
};

export const clearSyncQueue = () => {
  localStorage.setItem(SYNC_QUEUE_KEY, '[]');
};

export const syncOfflineData = async () => {
  const queue = getSyncQueue();
  if (queue.length === 0) return { synced: 0 };
  
  try {
    const response = await api.post('/sync/bulk', { operations: queue });
    clearSyncQueue();
    return response.data;
  } catch (error) {
    console.error('Sync failed:', error);
    throw error;
  }
};

export default api;
